import React from 'react'
import { Bell } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { TimerAlertsConfig } from '@/redux/slices/timerConfigurationSlice'

interface TimerAlertTriggersPanelProps {
  alerts: TimerAlertsConfig
  onAlertsChange: (next: TimerAlertsConfig) => void
  disabled: boolean
}

export const TimerAlertTriggersPanel: React.FC<TimerAlertTriggersPanelProps> = ({
  alerts,
  onAlertsChange,
  disabled,
}) => {
  return (
    <Card className='border border-sky-200 bg-sky-50/90 shadow-sm'>
      <CardHeader className='pb-2'>
        <div className='flex items-center gap-2'>
          <Bell className='h-5 w-5 text-sky-800' />
          <CardTitle className='text-lg text-dark-blue'>Alert Triggers</CardTitle>
        </div>
      </CardHeader>
      <CardContent className='space-y-6 pt-0'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div className='space-y-1 max-w-xl'>
            <Label className='text-sm font-medium text-slate-800'>
              Enable{' '}
              <span className='font-semibold'>5-minute warning alert (Visual)</span>
            </Label>
            <p className='text-xs text-slate-500'>
              Highlights time remaining and shows an icon when 5 minutes or less
              are left in the current phase.
            </p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='flex items-center gap-2 flex-shrink-0'>
                  <Switch
                    checked={alerts.enableFiveMinuteVisual}
                    onCheckedChange={(checked) =>
                      onAlertsChange({
                        ...alerts,
                        enableFiveMinuteVisual: checked,
                      })
                    }
                    disabled={disabled}
                    className='data-[state=checked]:bg-amber-500'
                  />
                  <span className='text-sm font-medium text-slate-600 w-9'>
                    {alerts.enableFiveMinuteVisual ? 'ON' : 'OFF'}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs'>
                <p>
                  Helps players notice when a phase is entering its final five
                  minutes.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <div className='space-y-1 max-w-xl'>
            <Label className='text-sm font-medium text-slate-800'>
              Enable{' '}
              <span className='font-semibold'>1-minute final alert (Visual)</span>
            </Label>
            <p className='text-xs text-slate-500'>
              Stronger visual emphasis when one minute or less remains in the
              phase.
            </p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='flex items-center gap-2 flex-shrink-0'>
                  <Switch
                    checked={alerts.enableOneMinuteVisual}
                    onCheckedChange={(checked) =>
                      onAlertsChange({
                        ...alerts,
                        enableOneMinuteVisual: checked,
                      })
                    }
                    disabled={disabled}
                    className='data-[state=checked]:bg-orange-500'
                  />
                  <span className='text-sm font-medium text-slate-600 w-9'>
                    {alerts.enableOneMinuteVisual ? 'ON' : 'OFF'}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs'>
                <p>
                  Makes the countdown highly visible in the last minute of a
                  phase.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <p className='text-xs text-slate-600 border-t border-sky-200/80 pt-4'>
          Alerts activate automatically based on remaining phase time. Save your
          choices with <strong>Save Timer Configuration</strong> on the
          Configuration tab (available before the game starts).
        </p>
      </CardContent>
    </Card>
  )
}
